import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { fetchAnimeData, fetchMangaData, fetchFavoriteCharacters, fetchAllProfileData } from "../api/profileAPI";
import ProfileHeader from "../components/profile/ProfileHeader";
import ProfileDetails from "../components/profile/ProfileDetails";
import ProfileStats from "../components/profile/ProfileStats";
import HeroBanner from "../components/profile/HeroBanner";
import CollectionTabs from "../components/profile/CollectionTabs";
import AnimeCollection from "../components/profile/AnimeCollection/AnimeCollection";
import MangaCollection from "../components/profile/MangaCollection/MangaCollection";
import CharacterList from "../components/profile/CharacterCollection/CharacterList";
import ProfileActions from "../components/profile/ProfileActions";
import ResponsiveSidebar from "../components/profile/ResponsiveSidebar";
import FriendList from "../components/profile/FriendList";
import "./Profile.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Helper for conditional logging
const logDebug = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(message, ...args);
  }
};

const Profile = () => {
  const { user, loading, updateUser } = useAuth();
  const navigate = useNavigate();
  
  // UI state with useRef where appropriate to avoid unnecessary re-renders
  const isMobileRef = useRef(window.innerWidth <= 768);
  const [isMobile, setIsMobile] = useState(isMobileRef.current);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const scrollPositionRef = useRef(0);
  const [headerClass, setHeaderClass] = useState('');
  
  // Collection state
  const [activeCollectionType, setActiveCollectionType] = useState('anime');
  const [activeAnimeTab, setActiveAnimeTab] = useState('watching');
  const [activeMangaTab, setActiveMangaTab] = useState('reading');
  
  // UI refs
  const fileInputRef = useRef(null);
  const profileFileInputRef = useRef(null);

  // Status configurations - memoized to prevent recreating them on each render
  const animeStatuses = useMemo(() => [
    { key: "watching", label: "Watching" },
    { key: "completed", label: "Completed" },
    { key: "planning", label: "Planning" },
    { key: "onHold", label: "On Hold" },
    { key: "dropped", label: "Dropped" }
  ], []);

  const mangaStatuses = useMemo(() => [
    { key: "reading", label: "Reading" },
    { key: "completed", label: "Completed" },
    { key: "planning", label: "Planning" },
    { key: "onHold", label: "On Hold" },
    { key: "dropped", label: "Dropped" }
  ], []);

  // FEATURE FLAG: Set to true to use unified API endpoint, false to use separate endpoints
  const useUnifiedEndpoint = true;

  // UNIFIED ENDPOINT APPROACH: Single API call for everything
  const { 
    data: profileData = { anime: {}, manga: {}, characters: [] },
    isLoading: isProfileDataLoading,
    isError: isProfileDataError
  } = useQuery({
    queryKey: ['profileAllData'],
    queryFn: fetchAllProfileData,
    enabled: !!user && useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // ORIGINAL APPROACH: Separate API calls
  // Anime Collection Queries
  const { 
    data: watchingAnime = [],
    isLoading: isWatchingLoading
  } = useQuery({
    queryKey: ['animeData', 'watching'],
    queryFn: () => fetchAnimeData('watching'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
  
  const { 
    data: completedAnime = [],
    isLoading: isCompletedAnimeLoading
  } = useQuery({
    queryKey: ['animeData', 'completed'],
    queryFn: () => fetchAnimeData('completed'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: planningAnime = [],
    isLoading: isPlanningAnimeLoading
  } = useQuery({
    queryKey: ['animeData', 'planning'],
    queryFn: () => fetchAnimeData('planning'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: onHoldAnime = [],
    isLoading: isOnHoldAnimeLoading
  } = useQuery({
    queryKey: ['animeData', 'onHold'],
    queryFn: () => fetchAnimeData('onHold'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: droppedAnime = [],
    isLoading: isDroppedAnimeLoading
  } = useQuery({
    queryKey: ['animeData', 'dropped'],
    queryFn: () => fetchAnimeData('dropped'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });

  // Manga Collection Queries
  const { 
    data: readingManga = [],
    isLoading: isReadingLoading
  } = useQuery({
    queryKey: ['mangaData', 'reading'],
    queryFn: () => fetchMangaData('reading'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: completedManga = [],
    isLoading: isCompletedMangaLoading
  } = useQuery({
    queryKey: ['mangaData', 'completed'],
    queryFn: () => fetchMangaData('completed'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: planningManga = [],
    isLoading: isPlanningMangaLoading
  } = useQuery({
    queryKey: ['mangaData', 'planning'],
    queryFn: () => fetchMangaData('planning'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: onHoldManga = [],
    isLoading: isOnHoldMangaLoading
  } = useQuery({
    queryKey: ['mangaData', 'onHold'],
    queryFn: () => fetchMangaData('onHold'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  const { 
    data: droppedManga = [],
    isLoading: isDroppedMangaLoading
  } = useQuery({
    queryKey: ['mangaData', 'dropped'],
    queryFn: () => fetchMangaData('dropped'),
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });
    
  // Characters Query
  const { 
    data: favoriteCharacters = [],
    isLoading: isCharactersLoading
  } = useQuery({
    queryKey: ['favoriteCharacters'],
    queryFn: fetchFavoriteCharacters,
    enabled: !!user && !useUnifiedEndpoint,
    staleTime: 5 * 60 * 1000
  });

  // Build collections based on which approach we're using
  const animeCollection = useMemo(() => {
    if (useUnifiedEndpoint) {
      return {
        watching: profileData?.anime?.watching || [],
        completed: profileData?.anime?.completed || [],
        planning: profileData?.anime?.planning || [],
        onHold: profileData?.anime?.onHold || [],
        dropped: profileData?.anime?.dropped || []
      };
    } else {
      return {
        watching: watchingAnime,
        completed: completedAnime,
        planning: planningAnime,
        onHold: onHoldAnime,
        dropped: droppedAnime
      };
    }
  }, [
    useUnifiedEndpoint, profileData?.anime, 
    watchingAnime, completedAnime, planningAnime, onHoldAnime, droppedAnime
  ]);
    
  const mangaCollection = useMemo(() => {
    if (useUnifiedEndpoint) {
      return {
        reading: profileData?.manga?.reading || [],
        completed: profileData?.manga?.completed || [],
        planning: profileData?.manga?.planning || [],
        onHold: profileData?.manga?.onHold || [],
        dropped: profileData?.manga?.dropped || []
      };
    } else {
      return {
        reading: readingManga,
        completed: completedManga,
        planning: planningManga,
        onHold: onHoldManga,
        dropped: droppedManga
      };
    }
  }, [
    useUnifiedEndpoint, profileData?.manga,
    readingManga, completedManga, planningManga, onHoldManga, droppedManga
  ]);
  
  const charactersData = useMemo(() => 
    useUnifiedEndpoint ? (profileData?.characters || []) : favoriteCharacters,
    [useUnifiedEndpoint, profileData?.characters, favoriteCharacters]
  );

  // Calculate overall loading state for display
  const isDataLoading = useUnifiedEndpoint 
    ? isProfileDataLoading
    : (isWatchingLoading || 
       isCompletedAnimeLoading || 
       isPlanningAnimeLoading || 
       isOnHoldAnimeLoading || 
       isDroppedAnimeLoading ||
       isReadingLoading ||
       isCompletedMangaLoading ||
       isPlanningMangaLoading ||
       isOnHoldMangaLoading ||
       isDroppedMangaLoading ||
       isCharactersLoading);

  // Image change handlers - using useCallback to avoid recreating these functions on each render
  const handleProfileImageChange = useCallback((e) => {
    logDebug("Profile image change triggered");
  }, []);

  const handleHeroImageChange = useCallback((e) => {
    logDebug("Hero image change triggered");
  }, []);
    
  // Check authentication
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);
    
  // Handle resize and scroll with debounce
  useEffect(() => {
    let resizeTimer;
    let scrollTimer;
    
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const mobile = window.innerWidth <= 768;
        isMobileRef.current = mobile;
        setIsMobile(mobile);
        if (!mobile) {
          setSidebarOpen(true);
        }
      }, 150); // Debounce for 150ms
    };
      
    const handleScroll = () => {
      // Store position in ref to avoid unnecessary state updates
      scrollPositionRef.current = window.scrollY;
      
      // Only update state if header class needs to change
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const newHeaderClass = scrollPositionRef.current > 100 ? 'sticky-header' : '';
        if (newHeaderClass !== headerClass) {
          setHeaderClass(newHeaderClass);
        }
      }, 100);
    };
      
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
      
    // Cleanup event listeners
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(resizeTimer);
      clearTimeout(scrollTimer);
    };
  }, [headerClass]);

  // Calculate counts for tabs - memoized to avoid recalculation on every render
  const totalCounts = useMemo(() => {
    return {
      anime: 
        (animeCollection.watching?.length || 0) +
        (animeCollection.completed?.length || 0) +
        (animeCollection.planning?.length || 0) +
        (animeCollection.onHold?.length || 0) +
        (animeCollection.dropped?.length || 0),
      
      manga:
        (mangaCollection.reading?.length || 0) +
        (mangaCollection.completed?.length || 0) +
        (mangaCollection.planning?.length || 0) +
        (mangaCollection.onHold?.length || 0) +
        (mangaCollection.dropped?.length || 0),
      
      characters: charactersData.length
    };
  }, [animeCollection, mangaCollection, charactersData]);
    
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prevState => !prevState);
  }, []);

  if (loading || !user) {
    return <div className="loading-spinner">🌀</div>;
  }

  return (
    <div className="profile-container">
      {/* Loading overlay for initial data load */}
      {isDataLoading && (
        <div className="loading-collection" style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000}}>
          <div className="loading-spinner"></div>
          <p>Loading your collections...</p>
        </div>
      )}

      {/* Mobile Header - shown only on mobile */}
      {isMobile && (
        <div className={`mobile-profile-header ${headerClass}`}>
          <button 
            className="sidebar-toggle" 
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            {sidebarOpen ? '×' : '☰'}
          </button>
          <div className="mobile-username">
            <h1>{user.username}</h1>
          </div>
        </div>
      )}
        
      <div className="profile-layout">
        {/* Responsive sidebar */}
        <ResponsiveSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
          <ProfileHeader 
            user={user}
            profileFileInputRef={profileFileInputRef}
            handleProfileImageChange={handleProfileImageChange}
          />
            
          <ProfileStats 
            animeCollection={animeCollection}
            mangaCollection={mangaCollection}
            favoriteCharacters={charactersData}
          /> 
          <ProfileDetails user={user} />
        </ResponsiveSidebar>

        {/* Main content area for collections */}
        <div className={`profile-main-content ${sidebarOpen && isMobile ? 'sidebar-open' : ''}`}>
          <HeroBanner 
            heroImage={user.heroImage}
            fileInputRef={fileInputRef}
            handleHeroImageChange={handleHeroImageChange}
          />
            
          {/* Collections Section */}
          <div className="favorites-section">
            <h2 className="favorites-title">My Collections</h2>
              
            <CollectionTabs 
              activeCollectionType={activeCollectionType}
              setActiveCollectionType={setActiveCollectionType}
              animeTotalCount={totalCounts.anime}
              mangaTotalCount={totalCounts.manga}
              charactersTotalCount={totalCounts.characters}
            />
              
            {/* Render the active collection type */}
            {activeCollectionType === 'anime' && (
              <AnimeCollection 
                collection={animeCollection}
                activeTab={activeAnimeTab}
                setActiveTab={setActiveAnimeTab}
                isLoading={false}
                statuses={animeStatuses}
              />
            )}
              
            {activeCollectionType === 'manga' && (
              <MangaCollection 
                collection={mangaCollection}
                activeTab={activeMangaTab}
                setActiveTab={setActiveMangaTab}
                isLoading={false}
                statuses={mangaStatuses}
              />
            )}
              
            {activeCollectionType === 'characters' && (
              <CharacterList 
                characters={charactersData}
                isLoading={false}
              />
            )}
          </div>

          {/* Friends Section */}
          <div className="favorites-section">
            <h2 className="favorites-title">My Friends</h2>
            <FriendList />
          </div>
        </div>
      </div>
        
      <ProfileActions />
    </div>
  );
};

export default React.memo(Profile);